import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ChevronDown, X, Plus, Loader2, AlertCircle, Search, Check } from 'lucide-react';
import axiosInstance from '../api/axios';

/**
 * Multi-select dropdown for picking canonical reports from the org's
 * `reports` catalog on the system create/edit page.
 *
 * Wire format:
 *   value      = string[]               // array of report UUIDs
 *   onChange   = (next: string[]) => void
 *
 * The system page passes the IDs straight through as `reportIds` on
 * POST /api/systems and PUT /api/systems/:id, where the backend updates
 * the `system_reports` junction via ReplaceLinksForSystem.
 *
 * "+ Add new report" navigates to /sox-reports/new with location.state
 * carrying { systemId, returnPath, addedReportName }. ReportCreate reads
 * that and (after save) routes back to the system page, where the newly
 * created report should already appear in the list because the system
 * page refetches /api/reports on focus / mount.
 *
 * Props:
 *   value         string[]                       — currently selected report UUIDs
 *   onChange      (next: string[]) => void
 *   error         boolean                        — render red border
 *   systemId      string | undefined             — passed to "+ Add new" so the
 *                                                  created report is auto-linked
 *                                                  back to this system
 *   returnPath    string                         — where ReportCreate should
 *                                                  navigate after a successful
 *                                                  save (usually the current page)
 *   prefilledName string                         — optional initial name to seed
 *                                                  the new-report form with
 *   listParams    object                         — extra GET /api/reports query params
 *   showAddNew    boolean                        — default true; hide "+ Add new report" when false
 *   placeholder   string                         — empty-state label in the closed control
 *   knownReports  { id, name }[]                 — optional seed labels (e.g. control GET key_reports)
 *                                                  so selected chips show names before/without list match
 */
function ReportMultiSelect({
  value,
  onChange,
  error,
  systemId,
  returnPath,
  prefilledName,
  listParams,
  showAddNew = true,
  placeholder = 'Select reports...',
  knownReports,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const rootRef = useRef(null);

  const selectedIds = useMemo(() => (Array.isArray(value) ? value : []), [value]);
  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  // Build a quick id -> report lookup so we can render chips for ids whose
  // report row hasn't been loaded yet (e.g. system row references a report
  // outside the current page of /api/reports). Seed knownReports first so
  // edit screens that already have id+name from GET can show names immediately;
  // the chip falls back to the id as a label until the row loads in.
  const reportsById = useMemo(() => {
    const map = new Map();
    const seed = Array.isArray(knownReports) ? knownReports : [];
    for (const r of seed) {
      if (r?.id) map.set(r.id, r);
    }
    for (const r of reports) {
      if (r?.id) map.set(r.id, r);
    }
    return map;
  }, [reports, knownReports]);

  // Fetch the catalog once on mount, looping pages so an org with > one page
  // of reports still gets the full list in the dropdown (matches the systems
  // page fetch). Re-runs on location.key so coming back from ReportCreate
  // shows the just-created report.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    (async () => {
      try {
        const PAGE_SIZE = 100;
        const all = [];
        let page = 1;
        // eslint-disable-next-line no-constant-condition
        while (true) {
          const res = await axiosInstance.get('/api/reports', {
            params: { page, page_size: PAGE_SIZE, ...(listParams || {}) },
          });
          if (cancelled) return;
          const batch = Array.isArray(res.data?.reports) ? res.data.reports : [];
          all.push(...batch);
          const totalPages = Number(res.data?.pagination?.total_pages) || 1;
          if (page >= totalPages || batch.length === 0) break;
          page += 1;
        }
        if (!cancelled) setReports(all);
      } catch (err) {
        if (!cancelled) setLoadError(err?.response?.data?.error || 'Failed to load reports');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key, JSON.stringify(listParams || {})]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const filteredReports = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return reports;
    return reports.filter((r) => {
      const name = (r.name || '').toLowerCase();
      const code = (r.reportId || '').toLowerCase();
      return name.includes(q) || code.includes(q);
    });
  }, [reports, query]);

  const toggleById = (id) => {
    if (!id) return;
    if (selectedSet.has(id)) {
      onChange(selectedIds.filter((x) => x !== id));
    } else {
      onChange([...selectedIds, id]);
    }
  };

  const removeById = (id) => {
    onChange(selectedIds.filter((x) => x !== id));
  };

  const goToCreate = () => {
    setOpen(false);
    navigate('/sox-reports/new', {
      state: {
        fromSystem: {
          systemId: systemId || null,
          returnPath: returnPath || `${location.pathname}${location.search || ''}`,
          // Lets ReportCreate seed the Name field with the user's query so
          // they don't have to retype it.
          prefilledName: query.trim() || prefilledName || '',
        },
      },
    });
  };

  // Surface an "Add new" hint when the user's search has no exact match.
  const exactMatch =
    query.trim().length > 0 &&
    reports.some((r) => (r.name || '').toLowerCase() === query.trim().toLowerCase());
  const showAddNewHint = query.trim().length > 0 && !exactMatch;

  return (
    <div ref={rootRef} className="relative">
      {/* Selected chips + dropdown toggle */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`w-full flex flex-wrap items-center gap-1.5 min-h-[42px] px-3 py-1.5 border rounded-lg bg-white text-left focus:outline-none focus:ring-2 focus:ring-blue-500 ${
          error ? 'border-red-500' : 'border-gray-300'
        }`}
      >
        {selectedIds.length === 0 ? (
          <span className="text-gray-400 text-sm">{placeholder}</span>
        ) : (
          selectedIds.map((id) => {
            const r = reportsById.get(id);
            const label = r?.name || id;
            const code = r?.reportId;
            return (
              <span
                key={id}
                className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-indigo-50 text-indigo-700 border border-indigo-200 rounded"
              >
                {label}
                {code ? <span className="text-indigo-400 font-mono">{code}</span> : null}
                <span
                  role="button"
                  tabIndex={0}
                  onClick={(e) => {
                    e.stopPropagation();
                    removeById(id);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      removeById(id);
                    }
                  }}
                  className="hover:bg-indigo-100 rounded p-0.5 inline-flex cursor-pointer"
                  aria-label={`Remove ${label}`}
                >
                  <X className="h-3 w-3" />
                </span>
              </span>
            );
          })
        )}
        <ChevronDown className="h-4 w-4 text-gray-400 ml-auto shrink-0" />
      </button>

      {open && (
        <div className="absolute z-30 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-72 overflow-hidden flex flex-col">
          {/* Search */}
          <div className="p-2 border-b border-gray-100">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search reports..."
                className="w-full pl-8 pr-2 py-1.5 text-sm bg-gray-50 border border-gray-200 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Options */}
          <div className="overflow-y-auto flex-1 min-h-0">
            {loading ? (
              <div className="px-3 py-6 text-center text-sm text-gray-500">
                <Loader2 className="h-4 w-4 animate-spin mx-auto mb-1" />
                Loading reports…
              </div>
            ) : loadError ? (
              <div className="px-3 py-4 text-sm text-red-600 flex items-center gap-2">
                <AlertCircle className="h-4 w-4 shrink-0" />
                {loadError}
              </div>
            ) : filteredReports.length === 0 ? (
              <div className="px-3 py-4 text-sm text-gray-500">
                {query ? `No reports match "${query}".` : 'No reports in the catalog yet.'}
              </div>
            ) : (
              filteredReports.map((r) => {
                const checked = selectedSet.has(r.id);
                return (
                  <button
                    key={r.id || r.reportId || r.name}
                    type="button"
                    onClick={() => toggleById(r.id)}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 text-left"
                  >
                    <span
                      className={`h-4 w-4 rounded border flex items-center justify-center shrink-0 ${
                        checked ? 'bg-blue-600 border-blue-600 text-white' : 'border-gray-300'
                      }`}
                      aria-hidden="true"
                    >
                      {checked && <Check className="h-3 w-3" />}
                    </span>
                    <span className="flex-1 min-w-0 truncate">{r.name}</span>
                    {r.reportId && (
                      <span className="text-xs text-gray-400 font-mono shrink-0">{r.reportId}</span>
                    )}
                  </button>
                );
              })
            )}
          </div>

          {showAddNew ? (
            <div className="border-t border-gray-100 p-1 bg-gray-50">
              <button
                type="button"
                onClick={goToCreate}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded text-blue-700 hover:bg-blue-50"
              >
                <Plus className="h-4 w-4" />
                {showAddNewHint ? <>Add new report <strong>“{query.trim()}”</strong></> : 'Add new report'}
              </button>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}

export default ReportMultiSelect;
